import queryString from 'query-string'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'

// SSO leaves the app for the identity provider and comes back through auth-service to
// /login?token=..., which carries no `next`. The return path waits in this tab's sessionStorage,
// which survives the round trip to other sites.
const SSO_RETURN_PATH_KEY = 'ssoReturnPath'

// Long enough for an SSO round trip. An older path belongs to an abandoned login and must not
// redirect a later one.
const SSO_RETURN_PATH_MAX_AGE_MS = 10 * 60 * 1000

/**
 * A post-login destination that is safe to navigate to, or null.
 *
 * `next` survives a round trip through the customer's own identity provider and
 * can also be read back out of this tab's sessionStorage, so by the time it is
 * navigated to, nothing about it has been checked. A crafted link would carry
 * someone through a genuine SSO sign-in and then bounce them off site, with the
 * Balto domain and a real login in the middle of the journey.
 *
 * Only a plain in-app path is allowed. A leading slash on its own is not enough
 * of a test -- `//evil.test` and `/\\evil.test` are both protocol-relative
 * URLs as far as a browser is concerned.
 *
 * auth-service applies the same rules to RelayState before it hands `next`
 * back (`safe_next_path` in saml_sso.py). Validating in only one place is not
 * enough: an IdP-initiated login supplies a RelayState the browser never sent,
 * and a saved path is read back from storage without passing through the
 * server at all.
 */
export const safeNextPath = (next) => {
  if (typeof next !== 'string') {
    return null
  }

  // Control characters first, because the prefix checks below look at fixed
  // positions and a browser does not. Tab, LF and CR are stripped from a URL
  // before it is parsed, so '/\t/evil.test' becomes '//evil.test' in the
  // browser while sailing past a second-character test here.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(next)) {
    return null
  }

  if (!next.startsWith('/')) {
    return null
  }

  if (next.startsWith('//') || next.includes('\\')) {
    return null
  }

  // Percent-encoding, refused rather than decoded and re-checked.
  // '/%5Cevil.test' passes every check above and becomes '/\evil.test' once
  // decoded, which a browser reads as a protocol-relative URL. An in-app path
  // has no need of a percent sign, so refusing one costs nothing.
  if (next.includes('%')) {
    return null
  }

  return next
}

/**
 * Adds `next` to a login-flow path, so the user still lands where they were headed.
 * @param {string} path e.g. '/login_sso'
 * @param {string|undefined} next Where to go after login
 * @returns {string}
 */
export const withNext = (path, next) =>
  isEmpty(next) ? path : `${path}?${queryString.stringify({ next })}`

/**
 * Remembers where to go after an SSO login. An empty `next` leaves a saved path alone: an SSO
 * error sends the user back to the SSO page without one, and the retry should still land where
 * the first attempt was headed.
 * @param {string|undefined} next Where to go after login
 */
export const saveSSOReturnPath = (next) => {
  const safe = safeNextPath(next)

  if (isEmpty(safe)) {
    return
  }

  try {
    sessionStorage.setItem(SSO_RETURN_PATH_KEY, JSON.stringify({ next: safe, savedAt: Date.now() }))
  } catch (error) {
    console.error(error)
  }
}

/**
 * The saved SSO return path, if it's fresh. It stays saved until a login succeeds, so a failed
 * attempt can be retried.
 * @returns {string|undefined} undefined when nothing fresh was saved
 */
export const peekSSOReturnPath = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SSO_RETURN_PATH_KEY))

    if (isNil(saved)) {
      return undefined
    }

    if (Date.now() - saved.savedAt > SSO_RETURN_PATH_MAX_AGE_MS) {
      sessionStorage.removeItem(SSO_RETURN_PATH_KEY)
      return undefined
    }

    // Re-checked on the way out: sessionStorage is writable by anything running
    // on this origin, and a value stored by an older build never met these
    // rules at all.
    return safeNextPath(saved.next) ?? undefined
  } catch (error) {
    console.error(error)
    return undefined
  }
}

/**
 * Forgets the saved SSO return path, once a login has succeeded.
 */
export const clearSSOReturnPath = () => {
  try {
    sessionStorage.removeItem(SSO_RETURN_PATH_KEY)
  } catch (error) {
    console.error(error)
  }
}
